import { defineMessages } from '@/hooks/useTranslation';

export default defineMessages({
  meta: {
    title: {
      de: 'Individuelle Business-Anwendungen | Maßgeschneiderte Software',
      en: 'Custom Business Applications | Tailored Software Solutions'
    },
    description: {
      de: 'Maßgeschneiderte Business-Anwendungen, die deine Geschäftsprozesse revolutionieren. Steigere Effizienz und ROI mit unseren individualisierten Softwarelösungen.',
      en: 'Custom business applications that revolutionize your business processes. Increase efficiency and ROI with our individualized software solutions.'
    }
  },
  header: {
    title: {
      de: 'Individuelle Business-Anwendungen',
      en: 'Custom Business Applications'
    },
    titleHighlight: {
      de: 'Business-Anwendungen',
      en: 'Business Applications'
    },
    description: {
      de: 'Maßgeschneiderte Software, die exakt auf deine Geschäftsprozesse zugeschnitten ist und deinem Team hilft, effizienter zu arbeiten.',
      en: 'Custom software tailored exactly to your business processes, helping your team work more efficiently.'
    },
    details: {
      de: 'Von der einfachen Produktivitätssteigerung bis zur kompletten Prozessautomatisierung – unsere Business-Anwendungen verbinden Benutzerfreundlichkeit mit leistungsstarker Funktionalität.',
      en: 'From simple productivity enhancements to complete process automation - our business applications combine user-friendliness with powerful functionality.'
    },
    primaryCTA: {
      de: 'Jetzt Erstgespräch vereinbaren',
      en: 'Schedule initial consultation now'
    },
    secondaryCTA: {
      de: 'Mehr erfahren',
      en: 'Learn more'
    },
    proofPoints: {
      experience: {
        de: '10+ Jahre Erfahrung in der Softwareentwicklung',
        en: '10+ years of software development experience'
      },
      clients: {
        de: '15+ zufriedene Kunden in DACH',
        en: '15+ satisfied clients in DACH region'
      },
      projects: {
        de: '3 eigene Produkte auf dem Markt',
        en: '3 own products on the market'
      }
    }
  },
  pain: {
    title: {
      de: 'Kennst du diese Herausforderungen?',
      en: 'Do you recognize these challenges?'
    },
    intro: {
      de: 'Vielleicht kennst du das: Dein Unternehmen wächst, aber die vorhandene Software hält nicht Schritt. Mitarbeiter umgehen bestehende Systeme mit Excel-Listen, doppelter Dateneingabe oder Insellösungen. Zeit und Ressourcen versickern in ineffizienten Prozessen, während die Frustration wächst.',
      en: "Maybe you know this: Your company is growing, but the existing software can't keep up. Employees bypass existing systems with Excel lists, double data entry, or island solutions. Time and resources are wasted in inefficient processes while frustration grows."
    },
    stats: {
      title: {
        de: 'Die harte Realität in Zahlen:',
        en: 'The hard reality in numbers:'
      },
      inefficiency: {
        de: '68% der Mitarbeiter verlieren täglich mehr als 1 Stunde durch ineffiziente Softwaresysteme',
        en: '68% of employees lose more than 1 hour daily due to inefficient software systems'
      },
      dataLoss: {
        de: '43% der Unternehmen berichten von Datenverlusten durch fehlerhafte Systemintegration',
        en: '43% of companies report data loss due to faulty system integration'
      },
      costOverruns: {
        de: '87% der IT-Projekte mit Standardsoftware überschreiten das Budget oder den Zeitplan',
        en: '87% of IT projects with standard software exceed budget or schedule'
      }
    },
    scrollCTA: {
      de: 'Entdecke die Lösung',
      en: 'Discover the solution'
    }
  },
  challenges: {
    title: {
      de: 'Häufige Herausforderungen',
      en: 'Common Challenges'
    },
    subtitle: {
      de: 'Diese typischen Probleme lassen sich mit maßgeschneiderten Business-Anwendungen lösen',
      en: 'These typical problems can be solved with custom business applications'
    },
    items: {
      outdatedUI: {
        title: {
          de: 'Veraltete Benutzeroberflächen',
          en: 'Outdated User Interfaces'
        },
        description: {
          de: 'Altmodische, komplizierte oder ineffiziente Benutzeroberflächen verlangsamen Arbeitsabläufe und frustrieren Mitarbeiter.',
          en: 'Old-fashioned, complicated or inefficient user interfaces slow down workflows and frustrate employees.'
        },
        solution: {
          de: 'Intuitive, moderne UI/UX-Konzepte mit optimierten Arbeitsabläufen und effizienter Dateneingabe.',
          en: 'Intuitive, modern UI/UX concepts with optimized workflows and efficient data entry.'
        }
      },
      dataSilos: {
        title: {
          de: 'Isolierte Datensilos',
          en: 'Isolated Data Silos'
        },
        description: {
          de: 'Informationen stecken in verschiedenen Systemen fest, die nicht miteinander kommunizieren können.',
          en: 'Information is stuck in different systems that cannot communicate with each other.'
        },
        solution: {
          de: 'Nahtlose Integration aller Systeme mit zentraler Datenhaltung und durchgängigem Datenfluss.',
          en: 'Seamless integration of all systems with central data storage and consistent data flow.'
        }
      },
      manualProcesses: {
        title: {
          de: 'Manuelle Prozesse',
          en: 'Manual Processes'
        },
        description: {
          de: 'Zeitaufwändige manuelle Eingaben, Berechnungen oder Datenübertragungen zwischen Systemen.',
          en: 'Time-consuming manual entries, calculations or data transfers between systems.'
        },
        solution: {
          de: 'Automatisierte Workflows und intelligente Prozesse, die manuelle Arbeiten minimieren.',
          en: 'Automated workflows and intelligent processes that minimize manual work.'
        }
      },
      dataAccess: {
        title: {
          de: 'Schwieriger Datenzugriff',
          en: 'Difficult Data Access'
        },
        description: {
          de: 'Wichtige Geschäftsdaten sind schwer zugänglich oder müssen umständlich manuell ausgewertet werden.',
          en: 'Important business data is difficult to access or needs to be evaluated manually in a complicated way.'
        },
        solution: {
          de: 'Intuitive Dashboards und Reporting-Tools mit Echtzeit-Daten und benutzerdefinierten Ansichten.',
          en: 'Intuitive dashboards and reporting tools with real-time data and custom views.'
        }
      },
      userAdoption: {
        title: {
          de: 'Geringe Benutzerakzeptanz',
          en: 'Low User Adoption'
        },
        description: {
          de: 'Vorhandene Systeme werden von Mitarbeitern umgangen oder nur widerwillig genutzt.',
          en: 'Existing systems are bypassed by employees or used reluctantly.'
        },
        solution: {
          de: 'Benutzerzentrierte Entwicklung mit regelmäßigem Feedback und kontinuierlicher Verbesserung.',
          en: 'User-centered development with regular feedback and continuous improvement.'
        }
      },
      scalability: {
        title: {
          de: 'Mangelnde Skalierbarkeit',
          en: 'Lack of Scalability'
        },
        description: {
          de: 'Bestehende Lösungen stoßen mit wachsendem Unternehmen und steigenden Anforderungen an ihre Grenzen.',
          en: 'Existing solutions reach their limits as the company grows and requirements increase.'
        },
        solution: {
          de: 'Zukunftssichere Architektur, die flexibel an veränderte Geschäftsanforderungen angepasst werden kann.',
          en: 'Future-proof architecture that can be flexibly adapted to changing business requirements.'
        }
      }
    }
  },
  service: {
    title: {
      de: 'Individuell entwickelte Business-Anwendungen',
      en: 'Individually developed business applications'
    },
    subtitle: {
      de: 'Dein Partner für maßgeschneiderte Softwarelösungen, die genau das tun, was dein Unternehmen braucht',
      en: 'Your partner for custom software solutions that do exactly what your business needs'
    },
    pitch: {
      de: 'Wir entwickeln Business-Anwendungen, die perfekt auf deine Geschäftsprozesse zugeschnitten sind. Keine Kompromisse, keine unnötigen Funktionen – nur Software, die dein Unternehmen wirklich voranbringt.',
      en: 'We develop business applications perfectly tailored to your business processes. No compromises, no unnecessary functions – just software that really moves your company forward.'
    },
    ai: {
      title: {
        de: 'Mit KI-Power schneller zum Ziel',
        en: 'Reach your goal faster with AI power'
      },
      description: {
        de: 'Durch moderne KI-Technologien erleben wir einen revolutionären Wandel in der Softwareentwicklung. Wir nutzen diese Innovationen, um deine Business-Anwendungen schneller, kosteneffizienter und mit höherer Qualität zu entwickeln. So profitierst du von kürzeren Entwicklungszyklen, besseren Ergebnissen und einem attraktiveren ROI.',
        en: 'With modern AI technologies, we are experiencing a revolutionary change in software development. We use these innovations to develop your business applications faster, more cost-efficiently and with higher quality. This way, you benefit from shorter development cycles, better results and a more attractive ROI.'
      },
      benefits: {
        speed: {
          title: {
            de: 'Schnellere Entwicklung',
            en: 'Faster Development'
          },
          description: {
            de: 'Bis zu 40% kürzere Entwicklungszyklen durch KI-gestützte Code-Generierung und automatisierte Prozesse.',
            en: 'Up to 40% shorter development cycles through AI-supported code generation and automated processes.'
          }
        },
        quality: {
          title: {
            de: 'Höhere Qualität',
            en: 'Higher Quality'
          },
          description: {
            de: 'Weniger Fehler durch intelligente Code-Analyse und automatische Qualitätssicherung.',
            en: 'Fewer errors through intelligent code analysis and automatic quality assurance.'
          }
        },
        algorithms: {
          title: {
            de: 'Bessere Algorithmen',
            en: 'Better Algorithms'
          },
          description: {
            de: 'Optimierte Prozesse und intelligentere Lösungen durch KI-basierte Algorithmen.',
            en: 'Optimized processes and smarter solutions through AI-based algorithms.'
          }
        },
        cost: {
          title: {
            de: 'Kosteneinsparung',
            en: 'Cost Savings'
          },
          description: {
            de: 'Attraktiverer ROI durch effizientere Entwicklungsprozesse und schnellere Time-to-Market.',
            en: 'More attractive ROI through more efficient development processes and faster time-to-market.'
          }
        }
      },
      cta: {
        de: 'KI-Revolution für dein Unternehmen nutzen',
        en: 'Use AI revolution for your business'
      }
    },
    forWhom: {
      de: 'Ideal für mittelständische Unternehmen, die spezielle Anforderungen haben, die von Standardsoftware nicht abgedeckt werden.',
      en: 'Ideal for medium-sized companies with special requirements not covered by standard software.'
    },
    whyNow: {
      de: 'In Zeiten digitaler Transformation ist maßgeschneiderte Software kein Luxus, sondern ein entscheidender Wettbewerbsvorteil. Wer jetzt handelt, sichert sich die digitale Zukunft.',
      en: 'In times of digital transformation, custom software is not a luxury but a decisive competitive advantage. Those who act now secure their digital future.'
    },
    demoCTA: {
      de: 'Unverbindliches Analysegespräch vereinbaren',
      en: 'Schedule a non-binding analysis meeting'
    }
  },
  proof: {
    title: {
      de: 'Was unsere Kunden sagen',
      en: 'What our customers say'
    },
    subtitle: {
      de: 'Erfahre, wie unsere Business-Anwendungen echte Probleme gelöst und messbare Erfolge gebracht haben',
      en: 'Find out how our business applications have solved real problems and brought measurable success'
    },
    testimonials: {
      first: {
        name: {
          de: 'Thomas Meyer',
          en: 'Thomas Meyer'
        },
        position: {
          de: 'Geschäftsführer, Meyer Industrietechnik GmbH',
          en: 'CEO, Meyer Industrial Technology GmbH'
        },
        quote: {
          de: 'Nach jahrelangem Kampf mit angepasster Standardsoftware haben wir uns für eine individuelle Lösung entschieden. Das Ergebnis übertrifft alle Erwartungen: Unsere Prozesse laufen 40% schneller, die Mitarbeiterzufriedenheit ist deutlich gestiegen, und wir sparen jährlich fünfstellige Beträge an Lizenzkosten.',
          en: 'After years of struggling with customized standard software, we decided on an individual solution. The result exceeds all expectations: Our processes run 40% faster, employee satisfaction has significantly increased, and we save five-figure amounts in license costs annually.'
        }
      },
      second: {
        name: {
          de: 'Sabine Krause',
          en: 'Sabine Krause'
        },
        position: {
          de: 'IT-Leiterin, Krause Logistik AG',
          en: 'IT Director, Krause Logistics AG'
        },
        quote: {
          de: 'Die maßgeschneiderte Logistik-Software hat unsere Lieferkettenprobleme praktisch über Nacht gelöst. Lagerbestände konnten um 23% reduziert werden, und die Liefertreue stieg von 87% auf 99%. Die Investition hat sich bereits nach 11 Monaten amortisiert.',
          en: 'The custom logistics software solved our supply chain problems practically overnight. Inventory could be reduced by 23%, and delivery reliability increased from 87% to 99%. The investment paid for itself after just 11 months.'
        }
      }
    },
    results: {
      title: {
        de: 'Messbare Ergebnisse',
        en: 'Measurable results'
      },
      efficiency: {
        number: {
          de: '35%',
          en: '35%'
        },
        text: {
          de: 'durchschnittliche Effizienzsteigerung',
          en: 'average efficiency increase'
        }
      },
      time: {
        number: {
          de: '47%',
          en: '47%'
        },
        text: {
          de: 'Reduktion manueller Arbeit',
          en: 'reduction in manual work'
        }
      },
      roi: {
        number: {
          de: '18',
          en: '18'
        },
        text: {
          de: 'Monate durchschnittlicher ROI',
          en: 'months average ROI'
        }
      }
    },
    clients: {
      title: {
        de: 'Diesen Unternehmen vertrauen uns bereits',
        en: 'These companies already trust us'
      }
    }
  },
  transformation: {
    title: {
      de: 'Von frustrierenden Standardlösungen zu maßgeschneiderter Effizienz',
      en: 'From frustrating standard solutions to customized efficiency'
    },
    subtitle: {
      de: 'Der Unterschied zwischen Kompromissen und echter Transformation',
      en: 'The difference between compromises and real transformation'
    },
    without: {
      title: {
        de: 'Ohne individuelle Business-Anwendungen',
        en: 'Without custom business applications'
      },
      items: {
        workarounds: {
          de: 'Ständige Workarounds und Behelfslösungen',
          en: 'Constant workarounds and makeshift solutions'
        },
        integration: {
          de: 'Teure und fehleranfällige Systemintegrationen',
          en: 'Expensive and error-prone system integrations'
        },
        data: {
          de: 'Datensilos und Informationsverluste',
          en: 'Data silos and information losses'
        },
        adaptation: {
          de: 'Anpassung der Prozesse an die Software',
          en: 'Adaptation of processes to the software'
        },
        costs: {
          de: 'Versteckte Folgekosten durch ineffiziente Prozesse',
          en: 'Hidden follow-up costs due to inefficient processes'
        },
        noDataCollection: {
          de: 'Daten werden gar nicht erfasst und damit gibt es keine Grundlage für Auswertungen oder Automatisierung',
          en: 'Data is not collected at all, resulting in no basis for analysis or automation'
        }
      }
    },
    with: {
      title: {
        de: 'Mit unseren maßgeschneiderten Lösungen',
        en: 'With our custom solutions'
      },
      items: {
        fit: {
          de: 'Software, die sich deinem Unternehmen anpasst, nicht umgekehrt',
          en: 'Software that adapts to your company, not vice versa'
        },
        integration: {
          de: 'Nahtlose Integration in deine IT-Landschaft',
          en: 'Seamless integration into your IT landscape'
        },
        data: {
          de: 'Zentraler Datenzugriff und 360°-Sicht auf alle Informationen',
          en: 'Central data access and 360° view of all information'
        },
        scaling: {
          de: 'Flexible Skalierung mit dem Wachstum deines Unternehmens',
          en: 'Flexible scaling with the growth of your company'
        },
        costs: {
          de: 'Nachhaltige Kosteneffizienz durch optimierte Prozesse',
          en: 'Sustainable cost efficiency through optimized processes'
        }
      }
    },
    quote: {
      text: {
        de: 'Software ist dafür da, deine bewährten Abläufe zu stärken – nicht, sie zu diktieren. Unternehmen, die ihre Prozesse über Jahre erfolgreich verfeinert haben, brauchen keine Revolution – sie brauchen eine Software, die sich nahtlos anpasst, statt alles auf den Kopf zu stellen.',
        en: 'Software should be a helpful tool for your processes in the company, not dictate the processes in your company. Especially for companies that have successfully introduced and lived processes for years, it makes sense to develop suitable software instead of turning the whole company upside down'
      },
      author: {
        de: 'Dennis Dittrich, Geschäftsführer, splusd-solutions',
        en: 'Dennis Dittrich, CEO, splusd-solutions'
      }
    }
  },
  process: {
    title: {
      de: 'Dein Weg zur perfekten Business-Anwendung',
      en: 'Your path to the perfect business application'
    },
    subtitle: {
      de: 'Ein klarer, bewährter Prozess, der Transparenz und Planungssicherheit bietet',
      en: 'A clear, proven process that offers transparency and planning security'
    },
    steps: {
      analysis: {
        title: {
          de: 'Analyse & Bedarfsermittlung',
          en: 'Analysis & Needs Assessment'
        },
        description: {
          de: 'Wir analysieren deine Geschäftsprozesse und Anforderungen im Detail. Was funktioniert gut? Wo gibt es Probleme? Was wünscht sich dein Team?',
          en: 'We analyze your business processes and requirements in detail. What works well? Where are there problems? What does your team want?'
        },
        duration: {
          de: '1-2 Wochen',
          en: '1-2 weeks'
        }
      },
      concept: {
        title: {
          de: 'Konzeption & Architektur',
          en: 'Conception & Architecture'
        },
        description: {
          de: 'Wir entwickeln ein maßgeschneidertes Lösungskonzept mit detaillierter technischer Architektur, UI/UX-Design und Datenmodell.',
          en: 'We develop a customized solution concept with detailed technical architecture, UI/UX design, and data model.'
        },
        duration: {
          de: '2-4 Wochen',
          en: '2-4 weeks'
        }
      },
      development: {
        title: {
          de: 'Agile Entwicklung',
          en: 'Agile Development'
        },
        description: {
          de: 'Wir entwickeln deine Anwendung in iterativen Sprints mit regelmäßigen Demos und Feedback-Runden, um schnell Ergebnisse zu liefern.',
          en: 'We develop your application in iterative sprints with regular demos and feedback rounds to deliver results quickly.'
        },
        duration: {
          de: '2-6 Monate',
          en: '2-6 months'
        }
      },
      testing: {
        title: {
          de: 'Test & Qualitätssicherung',
          en: 'Testing & Quality Assurance'
        },
        description: {
          de: 'Umfassende Tests sichern die Qualität und Stabilität deiner Anwendung. Dein Team wird in dieser Phase aktiv eingebunden.',
          en: 'Comprehensive tests ensure the quality and stability of your application. Your team is actively involved in this phase.'
        },
        duration: {
          de: '2-4 Wochen',
          en: '2-4 weeks'
        }
      },
      launch: {
        title: {
          de: 'Go-Live & Support',
          en: 'Go-Live & Support'
        },
        description: {
          de: 'Nach der erfolgreichen Einführung stehen wir dir mit Schulungen, Support und kontinuierlicher Weiterentwicklung zur Seite.',
          en: 'After successful implementation, we are at your side with training, support, and continuous development.'
        },
        duration: {
          de: 'Fortlaufend',
          en: 'Ongoing'
        }
      }
    },
    badges: {
      iso: {
        de: 'Made in Germany',
        en: 'Made in Germany'
      },
      scrum: {
        de: 'Scrum & agile Methoden',
        en: 'Scrum & agile methods'
      },
      security: {
        de: 'Datenschutz & IT-Sicherheit',
        en: 'Data protection & IT security'
      }
    }
  },
  packages: {
    title: {
      de: 'Unser Leistungsangebot',
      en: 'Our service offering'
    },
    subtitle: {
      de: 'Flexible Optionen, die sich deinem Bedarf anpassen',
      en: 'Flexible options that adapt to your needs'
    },
    starter: {
      title: {
        de: 'Business App Starter',
        en: 'Business App Starter'
      },
      price: {
        de: 'ab 15.000 €',
        en: 'from €15,000'
      },
      description: {
        de: 'Für kleinere Unternehmen mit klar umrissenen Anforderungen. Schnelle Umsetzung einzelner Geschäftsprozesse.',
        en: 'For smaller companies with clearly defined requirements. Quick implementation of individual business processes.'
      },
      features: {
        analysis: {
          de: 'Kompakte Anforderungsanalyse',
          en: 'Compact requirements analysis'
        },
        development: {
          de: 'Entwicklung einer Kernfunktionalität',
          en: 'Development of core functionality'
        },
        interface: {
          de: 'Einfache ERP-Schnittstelle',
          en: 'Simple ERP interface'
        },
        hosting: {
          de: 'Cloud- oder On-Premise-Hosting',
          en: 'Cloud or on-premises hosting'
        },
        support: {
          de: '3 Monate Support & Updates',
          en: '3 months of support & updates'
        }
      },
      cta: {
        de: 'Unverbindliches Angebot anfordern',
        en: 'Request a non-binding offer'
      }
    },
    professional: {
      title: {
        de: 'Business Suite Professional',
        en: 'Business Suite Professional'
      },
      price: {
        de: 'ab 35.000 €',
        en: 'from €35,000'
      },
      description: {
        de: 'Für mittelständische Unternehmen mit komplexeren Anforderungen. Umfassende Lösung für mehrere Geschäftsbereiche.',
        en: 'For medium-sized companies with more complex requirements. Comprehensive solution for multiple business areas.'
      },
      features: {
        analysis: {
          de: 'Umfassende Prozessanalyse',
          en: 'Comprehensive process analysis'
        },
        development: {
          de: 'Modularer Aufbau mit mehreren Funktionsbereichen',
          en: 'Modular structure with multiple functional areas'
        },
        interface: {
          de: 'Integration in bestehende IT-Landschaft',
          en: 'Integration into existing IT landscape'
        },
        reporting: {
          de: 'Dashboards & Reporting',
          en: 'Dashboards & reporting'
        },
        mobile: {
          de: 'Responsive Design / mobile Optimierung',
          en: 'Responsive design / mobile optimization'
        },
        support: {
          de: '12 Monate Support & Updates',
          en: '12 months of support & updates'
        }
      },
      cta: {
        de: 'Beratungstermin vereinbaren',
        en: 'Schedule consultation appointment'
      }
    },
    enterprise: {
      title: {
        de: 'Enterprise Solution',
        en: 'Enterprise Solution'
      },
      price: {
        de: 'Individuelles Angebot',
        en: 'Individual offer'
      },
      description: {
        de: 'Maßgeschneiderte Komplettlösung für große Unternehmen mit komplexen, abteilungsübergreifenden Anforderungen.',
        en: 'Customized complete solution for large companies with complex, cross-departmental requirements.'
      },
      features: {
        analysis: {
          de: 'Strategische IT-Beratung & Prozessoptimierung',
          en: 'Strategic IT consulting & process optimization'
        },
        development: {
          de: 'Umfassende Business Suite mit individuellen Modulen',
          en: 'Comprehensive business suite with individual modules'
        },
        integration: {
          de: 'Vollständige System- & Datenintegration',
          en: 'Complete system & data integration'
        },
        security: {
          de: 'Erweiterte Sicherheits- & Compliance-Features',
          en: 'Advanced security & compliance features'
        },
        ai: {
          de: 'KI-Komponenten & Automatisierung',
          en: 'AI components & automation'
        },
        support: {
          de: 'Individueller SLA mit 24/7 Support-Option',
          en: 'Individual SLA with 24/7 support option'
        }
      },
      cta: {
        de: 'Individuelles Angebot anfordern',
        en: 'Request individual offer'
      }
    },
    guarantee: {
      title: {
        de: 'Unsere Garantie',
        en: 'Our guarantee'
      },
      text: {
        de: 'Wir stehen zu 100% hinter unserer Arbeit. Wenn die entwickelte Lösung nicht die vereinbarten Anforderungen erfüllt, arbeiten wir kostenlos nach, bis du zufrieden bist.',
        en: 'We stand 100% behind our work. If the developed solution does not meet the agreed requirements, we will rework it for free until you are satisfied.'
      }
    },
    roi: {
      title: {
        de: 'Du glaubst, du kannst dir eine maßgeschneiderte Lösung nicht leisten?',
        en: 'You think you cannot afford a customized solution?'
      },
      description1: {
        de: 'Wir haben bereits mit einem Budget von 20.000 € ein erfolgreiches Projekt umgesetzt, das seitdem kontinuierlich weiterentwickelt wird – weil der Kunde den enormen Mehrwert täglich spürt.',
        en: 'We have already implemented a successful project with a budget of €20,000, which has been continuously developed since then - because the customer feels the enormous added value every day.'
      },
      description2: {
        de: 'Wenn du die tatsächlichen Kosten von Standardlösungen betrachtest, wird der Vorteil maßgeschneiderter Software schnell deutlich:',
        en: 'When you look at the actual costs of standard solutions, the advantage of custom software quickly becomes apparent:'
      },
      comparisonTitle: {
        de: 'Standard-CRM vs. Maßgeschneiderte Lösung',
        en: 'Standard CRM vs. Custom Solution'
      },
      standard: {
        title: {
          de: 'Große CRM-Anbieter',
          en: 'Large CRM Providers'
        },
        cost: {
          de: 'Ca. 1.500 € monatlich für umfassende Funktionen',
          en: 'Approx. €1,500 monthly for comprehensive features'
        },
        uselessFeatures: {
          de: 'Du bezahlst für Features, die du nie nutzen wirst',
          en: 'You pay for features you will never use'
        },
        limits: {
          de: 'Teure Zusatzkosten für höhere Kontakt- oder E-Mail-Limits',
          en: 'Expensive additional costs for higher contact or email limits'
        },
        workflow: {
          de: 'Keine individuelle Anpassung an deine Arbeitsabläufe',
          en: 'No individual adaptation to your workflows'
        },
        yearly: {
          de: '18.000 € jährlich ohne Eigentumsrechte',
          en: '€18,000 annually without ownership rights'
        }
      },
      custom: {
        title: {
          de: 'Maßgeschneiderte Business App',
          en: 'Custom Business App'
        },
        investment: {
          de: 'Einmalige Investition plus kleine Wartungspauschale',
          en: 'One-time investment plus small maintenance fee'
        },
        features: {
          de: 'Nur die Funktionen, die du wirklich brauchst',
          en: 'Only the features you really need'
        },
        costs: {
          de: 'Keine versteckten Kosten oder Nutzungslimits',
          en: 'No hidden costs or usage limits'
        },
        workflow: {
          de: 'Passt sich exakt deinen Arbeitsabläufen an',
          en: 'Adapts exactly to your workflows'
        },
        roi: {
          de: 'ROI häufig schon nach 12-18 Monaten erreicht',
          en: 'ROI often achieved after just 12-18 months'
        }
      },
      cta: {
        de: 'ROI-Berechnung für dein Projekt anfordern',
        en: 'Request ROI calculation for your project'
      }
    }
  },
  faq: {
    title: {
      de: 'Häufig gestellte Fragen',
      en: 'Frequently asked questions'
    },
    questions: {
      timeline: {
        question: {
          de: 'Wie lange dauert die Entwicklung einer individuellen Business-Anwendung?',
          en: 'How long does it take to develop a custom business application?'
        },
        answer: {
          de: 'Die Entwicklungsdauer hängt stark vom Umfang und der Komplexität ab. Einfache Anwendungen können in 2-3 Monaten realisiert werden, komplexere Projekte dauern typischerweise 4-8 Monate. Wir arbeiten agil und liefern kontinuierlich funktionsfähige Zwischenversionen, sodass du nicht bis zum Projektende warten musst, um erste Ergebnisse zu sehen.',
          en: "The development time depends heavily on the scope and complexity. Simple applications can be realized in 2-3 months, more complex projects typically take 4-8 months. We work agilely and continuously deliver functional intermediate versions, so you don't have to wait until the end of the project to see initial results."
        }
      },
      costs: {
        question: {
          de: 'Lohnt sich die Investition in eine maßgeschneiderte Lösung?',
          en: 'Is the investment in a custom solution worthwhile?'
        },
        answer: {
          de: 'Auf jeden Fall – wenn sie richtig geplant wird. Der Return on Investment entsteht durch Effizienzsteigerungen, Fehlerreduktion, Mitarbeiterzufriedenheit und strategische Vorteile. Unsere Kunden berichten von ROI-Zeiträumen zwischen 12 und 24 Monaten. Wir helfen dir gerne bei einer detaillierten Wirtschaftlichkeitsberechnung für dein spezifisches Projekt.',
          en: 'Definitely – if it is planned correctly. The return on investment comes from efficiency increases, error reduction, employee satisfaction, and strategic advantages. Our customers report ROI periods between 12 and 24 months. We are happy to help you with a detailed economic calculation for your specific project.'
        }
      },
      existing: {
        question: {
          de: 'Kann die neue Anwendung mit unseren bestehenden Systemen integriert werden?',
          en: 'Can the new application be integrated with our existing systems?'
        },
        answer: {
          de: 'Ja, wir legen großen Wert auf nahtlose Integration. Unsere Lösungen können mit praktisch allen gängigen ERP-, CRM- und anderen Geschäftssystemen verbunden werden – sei es über Standard-APIs, Webservices oder individuell entwickelte Schnittstellen. Die Integration bestehender Systeme ist ein zentraler Bestandteil unseres Entwicklungsprozesses.',
          en: 'Yes, we place great importance on seamless integration. Our solutions can be connected to virtually all common ERP, CRM, and other business systems – whether via standard APIs, web services, or individually developed interfaces. The integration of existing systems is a central component of our development process.'
        }
      },
      support: {
        question: {
          de: 'Wie sieht der Support nach der Fertigstellung aus?',
          en: 'What does support look like after completion?'
        },
        answer: {
          de: 'Nach Go-Live bieten wir verschiedene Support- und Wartungspakete an. Diese umfassen technischen Support, Fehlerbehebung, Sicherheitsupdates und kontinuierliche Optimierung. Wir verstehen uns als langfristiger Partner und stehen dir auch nach Projektabschluss mit Rat und Tat zur Seite, um den langfristigen Erfolg deiner Anwendung zu sichern.',
          en: 'After go-live, we offer various support and maintenance packages. These include technical support, bug fixing, security updates, and continuous optimization. We see ourselves as a long-term partner and are there for you even after project completion with advice and support to ensure the long-term success of your application.'
        }
      },
      technology: {
        question: {
          de: 'Welche Technologien kommen bei der Entwicklung zum Einsatz?',
          en: 'Which technologies are used in development?'
        },
        answer: {
          de: 'In den meisten Fällen setzen wir NodeJS und Golang ein mit den verschiedensten Datenbanken und Protokollen. Aber viel wichtiger ist die Frage nach den richtigen Problemen und Fachlichkeiten und Software wartungsarm zu programmieren. Damit der ROI gesichert ist. Wir wählen Technologien nicht nach dem neuesten Trend, sondern nach Stabilität, Wartbarkeit und langfristiger Zukunftssicherheit für dein Unternehmen.',
          en: 'In most cases, we use NodeJS and Golang with various databases and protocols. But much more important is the question of the right problems and business logic and programming software that is low-maintenance. So that the ROI is secured. We choose technologies not according to the latest trend, but according to stability, maintainability and long-term future security for your company.'
        }
      }
    },
    chatbotText: {
      de: 'Hast du weitere Fragen? Ruf uns gerne an!',
      en: 'Do you have more questions? Feel free to call us!'
    }
  },
  trust: {
    title: {
      de: 'Warum du uns vertrauen kannst',
      en: 'Why you can trust us'
    },
    guarantees: {
      dataProtection: {
        title: {
          de: 'Datenschutz-Garantie',
          en: 'Data protection guarantee'
        },
        description: {
          de: 'Deine Daten bleiben zu 100% in deiner Kontrolle. DSGVO-konforme Entwicklung und auf Wunsch Hosting in deutschen Rechenzentren.',
          en: 'Your data remains 100% under your control. GDPR-compliant development and, if desired, hosting in German data centers.'
        }
      },
      quality: {
        title: {
          de: 'Qualitäts-Versprechen',
          en: 'Quality promise'
        },
        description: {
          de: 'Alle Anwendungen durchlaufen ausführliche Testverfahren und Qualitätssicherung. Wir liefern erst, wenn alles einwandfrei funktioniert.',
          en: 'All applications undergo extensive testing procedures and quality assurance. We only deliver when everything works flawlessly.'
        }
      },
      transparency: {
        title: {
          de: 'Volle Transparenz',
          en: 'Full transparency'
        },
        description: {
          de: 'Du erhältst regelmäßige Projekt-Updates und hast jederzeit Einblick in den Fortschritt. Keine versteckten Kosten oder Überraschungen.',
          en: 'You receive regular project updates and have insight into progress at any time. No hidden costs or surprises.'
        }
      }
    },
    certifications: {
      title: {
        de: 'Zertifizierungen & Auszeichnungen',
        en: 'Certifications & awards'
      },
      items: {
        iso: {
          de: 'ISO 9001:2015 – Qualitätsmanagement',
          en: 'ISO 9001:2015 – Quality management'
        },
        iso27001: {
          de: 'ISO/IEC 27001 – Informationssicherheit',
          en: 'ISO/IEC 27001 – Information security'
        },
        award: {
          de: 'Top 100 Innovator 2023',
          en: 'Top 100 Innovator 2023'
        }
      }
    },
    press: {
      title: {
        de: 'In der Presse',
        en: 'In the press'
      }
    }
  },
  finalCTA: {
    title: {
      de: 'Bereit für deine maßgeschneiderte Business-Anwendung?',
      en: 'Ready for your customized business application?'
    },
    subtitle: {
      de: 'Lass uns gemeinsam deine digitale Transformation starten',
      en: "Let's start your digital transformation together"
    },
    primaryCTA: {
      de: 'Jetzt kostenloses Erstgespräch vereinbaren',
      en: 'Schedule free initial consultation now'
    },
    secondaryCTA: {
      de: 'Oder ruf uns an: 030 12345678',
      en: 'Or call us: 030 12345678'
    },
    privacyNote: {
      de: 'Deine Daten werden vertraulich behandelt. Es gelten unsere Datenschutzbestimmungen.',
      en: 'Your data will be treated confidentially. Our privacy policy applies.'
    }
  },
  exitIntent: {
    title: {
      de: 'Bevor du gehst...',
      en: 'Before you go...'
    },
    subtitle: {
      de: 'Sichere dir unseren kostenlosen Business-App-Guide',
      en: 'Secure our free business app guide'
    },
    description: {
      de: 'Erfahre in unserem 15-seitigen Leitfaden, worauf du bei der Planung einer Business-Anwendung achten solltest – inkl. Checkliste und ROI-Rechner.',
      en: 'Find out in our 15-page guide what you should pay attention to when planning a business application – incl. checklist and ROI calculator.'
    },
    cta: {
      de: 'Guide kostenlos herunterladen',
      en: 'Download guide for free'
    }
  }
}); 